import React from 'react';
import { Button, Alert, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FiSave, FiX, FiEye, FiEdit, FiTrash2 } from 'react-icons/fi';
import { formatDate } from '../../../utilities/common';

const ACTIVITY_TYPE_GROUPS = [
  {
    group: 'Fuel Combustion',
    options: [
      { value: 'DIESEL', label: 'Diesel' },
      { value: 'LNG', label: 'LNG' },
      { value: 'BIODIESEL', label: 'Biodiesel' },
      { value: 'GASOLINE', label: 'Gasoline' },
      { value: 'NATURAL_GAS_GENERAL', label: 'Natural Gas (General)' },
      { value: 'NATURAL_GAS_HVAC', label: 'Natural Gas (HVAC)' },
      { value: 'PROPANE', label: 'Propane' },
      { value: 'COAL', label: 'Coal' },
      { value: 'OTHER_COMBUSTIBLE_SOLIDS', label: 'Other Combustible Solids' },
      { value: 'OTHER_COMBUSTIBLE_LIQUIDS', label: 'Other Combustible Liquids' },
      { value: 'OTHER_COMBUSTIBLE_GASES', label: 'Other Combustible Gases' }
    ]
  },
  {
    group: 'Water Use',
    options: [
      { value: 'WATER_WITHDRAWN', label: 'Water Withdrawn' },
      { value: 'WATER_DISCHARGED', label: 'Water Discharged' },
      { value: 'WATER_RECYCLED', label: 'Water Recycled' }
    ]
  },
  {
    group: 'Electricity Use',
    options: [
      { value: 'ELECTRICITY_USE_GENERAL', label: 'Electricity Use (General)' },
      { value: 'ELECTRICITY_USE_HVAC', label: 'Electricity Use (HVAC)' },
      { value: 'ELECTRICITY_USE_PRODUCTION', label: 'Electricity Use (Production/Manufacturing)' },
      { value: 'SOLAR_PV_GENERATION', label: 'Solar PV Generation' },
      { value: 'LIGHTING', label: 'Lighting' },
      { value: 'ELECTRIC_CAR_CHARGING', label: 'Electric Car Charging' }
    ]
  },
  {
    group: 'Other HVAC',
    options: [
      { value: 'REFRIGERANT_USE', label: 'Refrigerant Use' },
      { value: 'DISTRICT_HEATING', label: 'District Heating' },
      { value: 'DISTRICT_COOLING', label: 'District Cooling' }
    ]
  },
  {
    group: 'Transportation',
    options: [
      { value: 'PETROL_CAR_VAN', label: 'Petrol Car/Van' },
      { value: 'DIESEL_CAR_VAN', label: 'Diesel Car/Van' },
      { value: 'ELECTRIC_CAR_VAN', label: 'Electric Car/Van' },
      { value: 'HYBRID_CAR_VAN', label: 'Hybrid Car/Van' },
      { value: 'PUBLIC_TRANSPORTATION', label: 'Public Transportation' },
      { value: 'WALKING_CYCLING', label: 'Walking/Cycling' },
      { value: 'FLIGHTS', label: 'Flights' },
      { value: 'MOTORCYCLE', label: 'Motorcycle' },
      { value: 'BOAT_FERRY', label: 'Boat/Ferry' }
    ]
  },
  {
    group: 'Waste',
    options: [
      { value: 'HAZARDOUS_WASTE', label: 'Hazardous Waste' },
      { value: 'NON_HAZARDOUS_WASTE', label: 'Non-Hazardous Waste' },
      { value: 'RECYCLED_WASTE', label: 'Recycled Waste' },
      { value: 'COMBUSTED_WASTE', label: 'Combusted Waste' }
    ]
  },
  {
    group: 'Freight',
    options: [
      { value: 'FREIGHT_BY_LAND', label: 'Freight by Land' },
      { value: 'FREIGHT_BY_SEA', label: 'Freight by Sea' },
      { value: 'FREIGHT_BY_AIR', label: 'Freight by Air' }
    ]
  },
  {
    group: 'Others',
    options: [
      { value: 'MATERIAL_INPUTS', label: 'Material Inputs' },
      { value: 'PURCHASED_GOODS', label: 'Purchased Goods' },
      { value: 'PURCHASED_SERVICES', label: 'Purchased Services' },
      { value: 'CAPITAL_GOODS', label: 'Capital Goods' }
    ]
  }
];

const ACTIVITY_TYPE_LABEL_MAP = ACTIVITY_TYPE_GROUPS.reduce((map, g) => {
  g.options.forEach((o) => {
    map[o.value] = o.label;
  });
  return map;
}, {});

const COLUMNS = [
  { key: 'invoiceNumber', label: 'Invoice Number', type: 'text', minWidth: '120px' },
  { key: 'provider', label: 'Provider', type: 'text', minWidth: '120px' },
  { key: 'currency', label: 'Currency', type: 'text', minWidth: '80px' },
  { key: 'meterNumber', label: 'Meter Number', type: 'text', minWidth: '120px' },
  { key: 'utility', label: 'Utility', type: 'text', minWidth: '100px' },
  { key: 'activityType', label: 'Activity Type', type: 'select', minWidth: '160px' },
  { key: 'periodStart', label: 'Period Start Date', type: 'date', minWidth: '140px', required: true },
  { key: 'periodEnd', label: 'Period End Date', type: 'date', minWidth: '140px', required: true },
  { key: 'contribution', label: 'Contribution', type: 'number', minWidth: '120px', required: true },
  { key: 'contributionUnit', label: 'Unit', type: 'text', minWidth: '100px', required: true },
  { key: 'cost', label: 'Cost', type: 'number', minWidth: '100px' }
];

const toTitleCase = (value) => {
  if (!value) return '—';
  return value
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const displayValue = (col, meter) => {
  if (col.key === 'periodStart' || col.key === 'periodEnd') {
    return formatDate(meter[col.key], '—');
  }
  if (col.type === 'select') {
    const raw = meter[col.key];
    return ACTIVITY_TYPE_LABEL_MAP[raw] || toTitleCase(raw) || '—';
  }
  return meter[col.key] || '—';
};

const MeterRow = ({
  meter,
  meterKey,
  groupIndex,
  meterIndex,
  isEditing,
  displayMeter,
  onEdit,
  onSaveMeterEdit,
  onCancelEdit,
  onDeleteClick,
  onFieldChange
}) => {
  const { t } = useTranslation();

  return (
    <tr style={{ verticalAlign: 'middle' }}>
      {COLUMNS.map((col) => (
        <td key={col.key}>
          {isEditing ? (
            col.type === 'select' ? (
              <Form.Control
                as="select"
                size="sm"
                value={displayMeter[col.key] || ''}
                onChange={(e) => onFieldChange(meterKey, col.key, e.target.value)}
              >
                <option value="">—</option>
                {displayMeter[col.key] && !ACTIVITY_TYPE_LABEL_MAP[displayMeter[col.key]] && (
                  <option value={displayMeter[col.key]}>{toTitleCase(displayMeter[col.key])}</option>
                )}
                {ACTIVITY_TYPE_GROUPS.map((g) => (
                  <optgroup key={g.group} label={g.group}>
                    {g.options.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </Form.Control>
            ) : (
              <Form.Control
                size="sm"
                type={col.type}
                value={displayMeter[col.key] || ''}
                onChange={(e) => onFieldChange(meterKey, col.key, e.target.value)}
              />
            )
          ) : (
            displayValue(col, displayMeter)
          )}
        </td>
      ))}
      <td>
        {isEditing ? (
          <div className="d-flex">
            <Button
              variant="link"
              size="sm"
              className="p-0 mr-2"
              onClick={() => onSaveMeterEdit(groupIndex, meterIndex)}
              title={t('Save')}
            >
              <FiSave size={18} className="text-success" />
            </Button>
            <Button variant="link" size="sm" className="p-0" onClick={() => onCancelEdit(meterKey)} title={t('Cancel')}>
              <FiX size={18} className="text-danger" />
            </Button>
          </div>
        ) : (
          <div className="d-flex">
            {displayMeter.viewUrl && (
              <Button
                variant="link"
                size="sm"
                className="p-0 mr-2"
                onClick={() => window.open(displayMeter.viewUrl, '_blank')}
                title={t('View Bill')}
              >
                <FiEye size={18} />
              </Button>
            )}
            <Button
              variant="link"
              size="sm"
              className="p-0 mr-2"
              onClick={() => onEdit(groupIndex, meterIndex, meter)}
              title={t('Edit')}
            >
              <FiEdit size={18} />
            </Button>
            <Button
              variant="link"
              size="sm"
              className="p-0"
              onClick={() => onDeleteClick(groupIndex, meterIndex)}
              title={t('Delete')}
            >
              <FiTrash2 size={18} className="text-danger" />
            </Button>
          </div>
        )}
      </td>
    </tr>
  );
};

const MeterTable = ({
  meters,
  groupIndex,
  editingMeters,
  onEdit,
  onSaveMeterEdit,
  onCancelEdit,
  onDeleteClick,
  onFieldChange
}) => {
  const { t } = useTranslation();

  if (!meters || meters.length === 0) {
    return <Alert variant="warning">{t('No meters found')}</Alert>;
  }

  return (
    <div className="table-responsive">
      <table className="table table-bordered" style={{ fontSize: '14px' }}>
        <thead className="thead-light">
          <tr style={{ whiteSpace: 'nowrap' }}>
            {COLUMNS.map((col) => (
              <th key={col.key} style={{ minWidth: col.minWidth }}>
                {t(col.label)}
                {col.required && <span className="text-danger">*</span>}
              </th>
            ))}
            <th style={{ minWidth: '100px' }}>{t('Actions')}</th>
          </tr>
        </thead>
        <tbody>
          {meters.map((meter, meterIndex) => {
            const meterKey = `${groupIndex}-${meterIndex}`;
            const isEditing = meterKey in editingMeters;
            const displayMeter = isEditing ? editingMeters[meterKey] : meter;

            return (
              <MeterRow
                key={meterIndex}
                meter={meter}
                meterKey={meterKey}
                groupIndex={groupIndex}
                meterIndex={meterIndex}
                isEditing={isEditing}
                displayMeter={displayMeter}
                onEdit={onEdit}
                onSaveMeterEdit={onSaveMeterEdit}
                onCancelEdit={onCancelEdit}
                onDeleteClick={onDeleteClick}
                onFieldChange={onFieldChange}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default MeterTable;
